import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { toAbsoluteUrl } from '../../functions';
import { Button } from '../../custom-essentials';
import MenuItem from './components/MenuItem';
import MenuDropdown from './components/MenuDropdown';

import {

} from '../../../actions';

function NavBar(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });

    const { permissions } = props;

    const [activeItem, setActiveItem] = useState(null);
    const setActiveWrapper = useCallback((e, newActiveItem) => {
        if (mounted.current) {
            if (activeItem === newActiveItem) setActiveItem(null);
            else setActiveItem(newActiveItem);
            e.stopPropagation();
        }
    }, [activeItem, setActiveItem]);

    const menuContents = useMemo(() => {
        const newMenuContents = {
            appointments: [],
            students: [],
            curriculum: [],
            accounts: [],
            admin: []
        };

        // Detect portal from domain
        const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
        const isReadingPlex = hostname.includes('readingplex');

        const isRoot = permissions === 'Root';
        const isAdmin = permissions === 'Admin';
        const isLead = permissions === 'Lead Instructor';
        const isInstructor = permissions === 'Instructor';

        if (isRoot || isAdmin || isLead || isInstructor) {
            // Appointments - ReadingPlex has fewer items
            if (isReadingPlex) {
                newMenuContents.appointments.push({ label: "Scheduling", to: "/appointments/scheduling", order: 1 });
                newMenuContents.appointments.push({ label: "Appointment Search", to: "/appointments/appointment-search", order: 2 });
            } else {
                // MathPlex appointments
                newMenuContents.appointments.push({ label: "Scheduling", to: "/appointments/scheduling", order: 1 });
                newMenuContents.appointments.push({ label: "Appointment Search", to: "/appointments/appointment-search", order: 2 });
                newMenuContents.appointments.push({ label: "Student Attendance", to: "/appointments/student-attendance", order: 3 });
                newMenuContents.appointments.push({ label: "Session Summaries", to: "/appointments/session-summaries", order: 4 });
            }

            // Curriculum pages
            if (isReadingPlex) {
                // ReadingPlex curriculum - Books, Book Lists, Book List Templates (per Guide Page 9)
                newMenuContents.curriculum.push({ label: "Books", to: "/curriculum/books", order: 1 });
                newMenuContents.curriculum.push({ label: "Book Lists", to: "/curriculum/book-lists", order: 2 });  // Page title: "Book List Manager"
                newMenuContents.curriculum.push({ label: "Book List Templates", to: "/curriculum/book-list-templates", order: 3 });  // Page title: "Book List Templates"
            } else {
                // MathPlex curriculum
                newMenuContents.curriculum.push({ label: "Flag Manager", to: "/curriculum/flag-manager", order: 1 });
            }

            // Student pages
            if (isReadingPlex) {
                // ReadingPlex students - Flag Manager, Group Manager, Contracts, Report Generator, Stamps Logs (per Guide Page 7)
                newMenuContents.students.push({ label: "Flag Manager", to: "/curriculum/flag-manager", order: 1 });
                newMenuContents.students.push({ label: "Group Manager", to: "/students/group-manager", order: 2 });
                newMenuContents.students.push({ label: "Contracts and Payments", to: "/students/contracts", order: 3 });
                newMenuContents.students.push({ label: "Report Generator / Progress Reports", to: "/students/report-generator", order: 4 });
                newMenuContents.students.push({ label: "Stamps Logs", to: "/students/stamps-logs", order: 5 });
            } else {
                // MathPlex students
                newMenuContents.students.push({ label: "Upcoming Exams", to: "/students/upcoming-exams", order: 1 });
                newMenuContents.students.push({ label: "Stamps Logs", to: "/students/stamps-logs", order: 2 });
            }

            // Admin pages - common to both
            newMenuContents.admin.push({ label: "Timesheets", to: "/admin/timesheets", order: 1 });
            newMenuContents.admin.push({ label: "Instructor Dashboard", to: "/admin/instructor-dashboard", order: 2 });
            newMenuContents.admin.push({ label: "Daily Overview", to: "/admin/daily-overview", order: 3 });
        }
        if (isRoot || isAdmin || isLead) {
            // Appointments - Auto Scheduler for both
            newMenuContents.appointments.push({ label: "Auto Scheduler", to: "/appointments/auto-scheduler", order: isReadingPlex ? 3 : 5 });

            // Student pages
            if (!isReadingPlex) {
                // MathPlex-only student pages
                newMenuContents.students.push({ label: "Contracts and Payments", to: "/students/contracts-payments", order: 3 });
                newMenuContents.students.push({ label: "Report Generator / Progress Reports", to: "/students/report-generator", order: 4 });
                newMenuContents.students.push({ label: "Holds Logs", to: "/students/holds-logs", order: 5 });
                newMenuContents.students.push({ label: "Transitions", to: "/students/transitions", order: 6 });
            }

            // Curriculum pages
            if (isReadingPlex) {
                // ReadingPlex curriculum - Book List Templates is available for Root/Admin/Lead
                // (Already added above for all users)
            } else {
                // MathPlex curriculum
                newMenuContents.curriculum.push({ label: "Assessment Manager", to: "/curriculum/assessment-manager", order: 2 });
                newMenuContents.curriculum.push({ label: "Lesson Manager", to: "/curriculum/lesson-manager", order: 3 });
                newMenuContents.curriculum.push({ label: "LP Manager", to: "/curriculum/lp-manager", order: 4 });
            }

            // Accounts
            newMenuContents.accounts.push({ label: "Members", to: "/accounts/members", order: 2 });
            if (isReadingPlex) {
                newMenuContents.accounts.push({ label: "Member Requests", to: "/accounts/member-requests", order: 3 });
            }

            // Admin pages
            if (!isReadingPlex) {
                // MathPlex-only admin pages
                newMenuContents.admin.push({ label: "Text and Email Logs", to: "/admin/text-email-logs", order: 4 });
                newMenuContents.admin.push({ label: "ACD Report", to: "/admin/acd-report", order: 5 });
            }
        }
        if (isRoot || isAdmin) {
            // Appointments - Scheduler Settings only for MathPlex
            if (!isReadingPlex) {
                newMenuContents.appointments.push({ label: "Scheduler Settings", to: "/appointments/settings", order: 6 });
            }

            // Curriculum pages
            if (!isReadingPlex) {
                newMenuContents.curriculum.push({ label: "Assessment Templates", to: "/curriculum/assessment-template-manager", order: 5 });
            }

            // Accounts
            newMenuContents.accounts.push({ label: "Employees", to: "/accounts/employees", order: 1 });
        }

        return newMenuContents;
    }, [permissions])

    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    // Member portal: must include 'member.' subdomain
    const isMemberPortal = hostname.includes('member.');

    return (
        <div>
            {/* <div className="fixed inset-0 pointer-events-auto" onClick={() => setActiveItem('')}/> */}
            <div className="sticky top-0 w-screen bg-black flex flex-wrap md:flex-nowrap z-[2000] min-h-16">
                {/* Main NavBar */}
                <div className="flex flex-row gap-x-2 md:gap-x-5 flex-1 min-w-0" onClick={() => setActiveItem('')}>
                    <div className="relative flex items-center gap-1 md:gap-2 shrink-0">
                        {isMemberPortal ? (
                            // Member portal: show both logos (smaller on mobile)
                            <>
                                <img
                                    alt="MathPlex Logo"
                                    className="h-6 md:h-10 mt-2 mx-1 md:mx-2"
                                    src={toAbsoluteUrl("/media/logos/darklong.png")}
                                />
                                <img
                                    alt="ReadingPlex Logo"
                                    className="h-6 md:h-10 mt-2"
                                    src={toAbsoluteUrl("/media/logos/readingplex-logo.png")}
                                />
                            </>
                        ) : (
                            // Admin portal: show single logo based on domain
                            <img
                                alt="logo"
                                className="h-8 md:h-12 mt-2 mx-2 md:mx-4"
                                src={toAbsoluteUrl(
                                    hostname.includes('readingplex')
                                        ? "/media/logos/readingplex-logo.png"
                                        : "/media/logos/darklong.png"
                                )}
                            />
                        )}
                    </div>
                    {/* Hide all navigation menu items for member portal */}
                    {!isMemberPortal && (
                        <>
                            <div className="flex shrink-0">
                                <MenuItem
                                    label="Home"
                                    to="/home"
                                />
                            </div>
                            <div className={`hidden md:flex ${!menuContents.appointments.length ? 'hidden' : ''}`}>
                                <MenuDropdown
                                    label="Appointments"
                                    items={menuContents.appointments}
                                    active={activeItem === 'appointments'}
                                    handleClick={(e) => setActiveWrapper(e, 'appointments')}
                                />
                            </div>
                            <div className={`hidden md:flex ${!menuContents.students.length ? 'hidden' : ''}`}>
                                <MenuDropdown
                                    label="Students"
                                    items={menuContents.students}
                                    active={activeItem === 'students'}
                                    handleClick={(e) => setActiveWrapper(e, 'students')}
                                />
                            </div>
                            <div className={`hidden md:flex ${!menuContents.curriculum.length ? 'hidden' : ''}`}>
                                <MenuDropdown
                                    label="Curriculum"
                                    items={menuContents.curriculum}
                                    active={activeItem === 'curriculum'}
                                    handleClick={(e) => setActiveWrapper(e, 'curriculum')}
                                />
                            </div>
                            <div className={`hidden md:flex ${!menuContents.accounts.length ? 'hidden' : ''}`}>
                                <MenuDropdown
                                    label="Accounts"
                                    items={menuContents.accounts}
                                    active={activeItem === 'accounts'}
                                    handleClick={(e) => setActiveWrapper(e, 'accounts')}
                                />
                            </div>
                            <div className={`hidden md:flex ${!menuContents.admin.length ? 'hidden' : ''}`}>
                                <MenuDropdown
                                    label="Admin"
                                    items={menuContents.admin}
                                    active={activeItem === 'admin'}
                                    handleClick={(e) => setActiveWrapper(e, 'admin')}
                                />
                            </div>
                        </>
                    )}
                </div>
                <div className="mr-2 md:mr-8 self-center shrink-0">
                    <Button
                        color="hol-mpLRed"
                        onClick={() => null}
                        className="text-xs md:text-sm px-2 md:px-4 py-1 md:py-2"
                    >
                        <Link to="/logout" className="whitespace-nowrap">
                            Log Out
                        </Link>
                    </Button>
                </div>
            </div>
            {/* Post-Navbar Spacer
            <div className="pb-12 bg-mpLYellow-5"/> */}
        </div>
    );
}

export default connect(null, {

})(NavBar);